import { FuseNavigationItem } from '@fuse/components/navigation';
import { isSystemOrg } from 'app/core/constants/system-org.constants';
import {
    FEATURE_FLAGS,
    FeatureFlag,
} from 'app/core/services/feature-flag.service';
import {
    canUseSupportTickets,
    hasAnyRole,
    ORG_ADMIN_AREA_ROLES,
    ORG_OWNER_TIER_ROLES,
    PLATFORM_STAFF_ROLES,
    ROLE_GROUPS,
    ROLES,
} from 'app/shared/constants';
import { environment } from 'environments/environment';

/**
 * Client-side nav fallback when Menu API returns empty / fails.
 *
 * Must stay aligned with BE `PhotoFlowMenuSeedData.OrgResource` / `AdminResource`
 * for the three first-class roles: SUPER_ADMIN, ADMIN, PHOTOGRAPHER.
 *
 * IAM admin surfaces (users, RBAC, org structure, SSO, profile/security/sessions)
 * are owned by nera-iam-portal — only an external "Identity & security" link is kept.
 * PhotoQA is merged from resource `photoqa-central` by NavigationService — not here.
 *
 * Titles are `nav.fallback.*` translation KEYS, not display text — the builders stay
 * pure and synchronous, and `localizeNavTitles()` resolves them where an injector is
 * available (NavigationService). The app defaults to Vietnamese, so hardcoded English
 * titles here would surface an English sidebar to most users whenever the Menu API is
 * down — precisely when the fallback matters.
 */

export function basic(
    id: string,
    title: string,
    link: string,
    icon: string
): FuseNavigationItem {
    return { id, title, type: 'basic', link, icon };
}

/**
 * Sister-shell deep link (IAM / SA). Same tab — one product, one browser
 * session; `_blank` made PhotoFlow feel like three separate apps.
 */
function sisterShell(
    id: string,
    title: string,
    url: string,
    icon: string
): FuseNavigationItem {
    return {
        id,
        title,
        type: 'basic',
        link: url,
        externalLink: true,
        // Same tab: preserve SSO cookie context and "one system" mental model.
        target: '_self',
        icon,
    };
}

export function group(
    id: string,
    title: string,
    children: FuseNavigationItem[]
): FuseNavigationItem {
    return { id, title, type: 'group', children };
}

function iamPortalBase(): string {
    return environment.iamPortalUrl.replace(/\/$/, '');
}

const iamPortalItem = (): FuseNavigationItem =>
    sisterShell(
        'iam-portal',
        'nav.fallback.identitySecurity',
        iamPortalBase(),
        'heroicons_outline:key'
    );
const iamSecurityItem = (): FuseNavigationItem =>
    sisterShell(
        'iam-security',
        'nav.fallback.securitySessions',
        `${iamPortalBase()}/me`,
        'heroicons_outline:lock-closed'
    );
const billingItem = (): FuseNavigationItem =>
    basic(
        'billing',
        'nav.fallback.billing',
        '/billing',
        'heroicons_outline:banknotes'
    );

function saPortalBase(): string {
    return environment.saPortalUrl.replace(/\/$/, '');
}

/**
 * photoflow-org fallback — mirrors BE seed role gates:
 * - photographer → Work (albums + files)
 * - admin / super_admin (dual hat) → Work + Organization
 * - all authenticated → Account
 *
 * `isOrgAdmin` is the org-scoped flag hydrated from `me/permissions`
 * (`IUser.isAdmin`): a role the user holds in ANOTHER org must not unlock the
 * Organization group here. Pass `undefined` (pre-hydration / hydration
 * failure) to fall back to inferring admin-ness from the flat role list.
 * Feature flags are NOT applied here: run the result through
 * `filterNavByFeatureFlags()`.
 */
export function buildPhotoflowOrgFallbackNav(
    userRoles: string | string[] | undefined,
    isOrgAdmin?: boolean,
    orgId?: string
): FuseNavigationItem[] {
    // #3806: identity-service's org-scoped `isAdmin` (OrgTierResolver) is
    // true for ORG_OWNER only — ORG_OPERATOR is not "admin tier" there,
    // although every admin-area route (ALL_ADMINS) and API it backs
    // (invitations, groups/departments via AdminOrPlatformAdmin) admits the
    // operator. Gating the group on that flag alone hid "Tổ chức" from an
    // operator who could still open each page by URL. After hydration
    // `userRoles` are the ACTING org's roles (me/permissions), so an
    // ORG_ADMIN_AREA role here is not a hat from another org.
    const holdsOrgAdminAreaRole = hasAnyRole(userRoles, [
        ...ORG_ADMIN_AREA_ROLES,
    ]);
    const isAdmin =
        isOrgAdmin === undefined
            ? holdsOrgAdminAreaRole ||
              hasAnyRole(userRoles, [...PLATFORM_STAFF_ROLES])
            : isOrgAdmin || holdsOrgAdminAreaRole;
    // Deliberately SUPER_ADMIN only (not PLATFORM_STAFF_ROLES) — this flag only
    // gates the `/communication` sa-portal deep link below (line ~260), and
    // that page is not in sa-portal's `platformAdminVisible` allowlist.
    // Widening this would recreate the exact dead-end-link problem the
    // comment down there already describes, just for PLATFORM_ADMIN instead
    // of a plain org admin.
    const isSuperAdmin = hasAnyRole(userRoles, [ROLES.SUPER_ADMIN]);
    // Marketplace is personal-session only (SystemOrg acting org) — mirrors
    // `personalSessionGuard` on the marketplace shell (marketplace.routes.ts).
    const isPersonalMarketplaceUser = isSystemOrg(orgId);
    const isPhotographer = hasAnyRole(userRoles, [
        ROLES.PHOTOGRAPHER,
        ...ORG_ADMIN_AREA_ROLES,
        ...PLATFORM_STAFF_ROLES,
    ]);

    const work: FuseNavigationItem[] = [];
    if (isPhotographer) {
        // Admins match isPhotographer too, and the Organization group below adds
        // its own /overview entry. Adding this one as well gave them two sidebar
        // rows for the same page — and two items sharing the id 'overview',
        // which Fuse keys on.
        if (!isAdmin) {
            work.push(
                basic(
                    'overview',
                    'nav.fallback.overview',
                    '/overview',
                    'heroicons_outline:squares-2x2'
                )
            );
        }
        work.push(
            basic(
                'albums',
                'nav.fallback.albums',
                '/albums',
                'heroicons_outline:photo'
            ),
            // WS-I (D16, MR-I1/MR-F6, PFL-2566): Customer directory.
            basic(
                'customers',
                'nav.fallback.customers',
                '/customers',
                'heroicons_outline:user-group'
            ),
            // One product area (files / media), two technical backends:
            //   /dam     → document library (DAM metadata, versions, tags)
            //   /storage → cloud folders + quota (Google Drive-style storage)
            {
                id: 'files',
                title: 'nav.fallback.files',
                type: 'collapsable',
                icon: 'heroicons_outline:folder-open',
                children: [
                    ...(hasAnyRole(userRoles, [
                        ...ROLE_GROUPS.DAM_DOCUMENT_READERS,
                    ])
                        ? [
                              basic(
                                  'documents',
                                  'nav.fallback.documents',
                                  '/dam',
                                  'heroicons_outline:document-duplicate'
                              ),
                          ]
                        : []),
                    basic(
                        'cloud-storage',
                        'nav.fallback.cloudStorage',
                        '/storage',
                        'heroicons_outline:cloud'
                    ),
                ],
            }
        );
    }

    // Studio registration is photographer self-service only (not org ADMIN manage).
    // Approve/reject stays SuperAdmin-only in nera-sa-portal.
    if (hasAnyRole(userRoles, [ROLES.PHOTOGRAPHER])) {
        // Only from the personal workspace: inside a studio org it is already open (#924).
        if (isSystemOrg(orgId)) {
            work.push(
                basic(
                    'register-studio',
                    'nav.fallback.registerStudio',
                    '/org-registration/request',
                    'heroicons_outline:building-storefront'
                )
            );
        }
        // A SOLO SystemOrg photographer subscribes to and pays for their own
        // workspace, so they need the same /billing page (plan, cancel, payments)
        // an admin has. Admin dual-hats already get it from the Organization group
        // below (same id). #61 security fix: a STUDIO photographer must NOT get
        // this link — WS-B §7.2 restricts studio billing to ORG_OWNER/platform
        // staff, matching BillingAccessGuard's route gate.
        if (!isAdmin && isSystemOrg(orgId)) {
            work.push(billingItem());
        }
    }

    // WS-F (D27, MR-F4): the editor-pool screen ("Việc chờ chỉnh sửa"). A pure
    // EDITOR (no Photographer/admin hat, so the `isPhotographer` block above
    // never ran) gets no other /albums nav entry at all — this is where they
    // land instead. A dual-hat user (Photographer+Editor, or an admin who is
    // also seeded EDITOR) already has full /albums access and does not need a
    // separate pool link.
    if (!isPhotographer && hasAnyRole(userRoles, [ROLES.EDITOR])) {
        work.push(
            basic(
                'editor-pool',
                'nav.fallback.editorPool',
                '/editor-pool',
                'heroicons_outline:inbox-stack'
            )
        );
    }

    const admin: FuseNavigationItem[] = [];
    if (isAdmin) {
        admin.push(
            basic(
                'overview',
                'nav.fallback.orgOverview',
                '/overview',
                'heroicons_outline:building-office-2'
            ),
            basic(
                'studio-settings',
                'nav.fallback.studioSettings',
                '/studio-settings',
                'heroicons_outline:cog-6-tooth'
            ),
            // #3806: both pages were reachable only by typing the URL. Same
            // gate as the route (`admin-feature-core.routes.ts`): ALL_ADMINS.
            basic(
                'org-structure',
                'nav.fallback.orgStructure',
                '/org-structure',
                'heroicons_outline:rectangle-group'
            ),
            // Studio Manager's route (and be-app StudioController) is
            // ORG_OWNER/SUPER_ADMIN only — an operator gets no dead link.
            ...(hasAnyRole(userRoles, [...ROLE_GROUPS.ORG_OWNER_OR_SUPER_ADMIN])
                ? [
                      basic(
                          'studio-manager',
                          'nav.fallback.studioManager',
                          '/studio-manager',
                          'heroicons_outline:briefcase'
                      ),
                  ]
                : []),
            // RBAC (roles/permissions/groups) lives on the IAM shell —
            // fe-web's own `/rbac` port (PFL-1637) was found to duplicate an
            // already-active nera-iam-portal admin surface hitting the same
            // Iam.API endpoints and was removed; deep-link into iam-portal's
            // `/roles` instead of the generic `iam-portal` shell entry below.
            sisterShell(
                'rbac',
                'nav.fallback.rbac',
                `${iamPortalBase()}/roles`,
                'heroicons_outline:shield-check'
            ),
            // Identity UI lives on the IAM shell — product name, not stack name.
            iamPortalItem()
        );
        // PFL-1864: one entry, not two. `/subscription` was a duplicate page
        // (same subscription + plan catalog + subscribe/cancel) and is now a
        // redirect into `/billing`, which owns both halves. #61 security fix:
        // `isAdmin` also admits ORG_OPERATOR (ORG_ADMIN_AREA_ROLES), but WS-B
        // §7.2 restricts a studio's billing to ORG_OWNER/platform staff — an
        // operator gets every other admin nav item above except this one.
        // Matches BillingAccessGuard's route gate exactly.
        if (
            isSystemOrg(orgId) ||
            hasAnyRole(userRoles, [
                ...ORG_OWNER_TIER_ROLES,
                ...PLATFORM_STAFF_ROLES,
            ])
        ) {
            admin.push(billingItem());
        }
    }

    // Chatbot knowledge-base management is platform staff only — org
    // owner/operator can *use* the chatbot but cannot manage KBs, and
    // chatbot-service's `admin` policy action enforces the same set
    // server-side. Deliberately outside the `isAdmin` block above, which
    // would also show it to org roles.
    if (hasAnyRole(userRoles, [...PLATFORM_STAFF_ROLES])) {
        admin.push(
            basic(
                'chatbot',
                'nav.fallback.chatbot',
                '/chatbot',
                'heroicons_outline:chat-bubble-left-right'
            )
        );
    }

    // PFL-2235: communication admin (sender, templates, providers, send stats,
    // email suppressions) moved out of fe-web onto nera-sa-portal's
    // `/communication`. Deep-linked the same way `rbac` deep-links into
    // iam-portal's `/roles`, rather than pointing at the sa-portal shell root.
    //
    // SUPER_ADMIN only, deliberately. fe-web's deleted modules were gated
    // `ROLE_GROUPS.ALL_ADMINS`, but sa-portal's entire `(control-panel)` tree
    // sits behind `SuperOrgAdminGuard` and `/communication` is registered in
    // `SUPER_ADMIN_NAV_ITEMS` — a plain org ADMIN who followed this link would
    // land on sa-portal's "Access Restricted" page. Showing it only to roles
    // that can actually open it is better than a link that dead-ends; the
    // consequence, that org admins keep no communication-admin surface at all,
    // is a product decision recorded on PFL-2235, not something a nav entry can
    // paper over.
    if (isSuperAdmin) {
        admin.push(
            sisterShell(
                'communication-admin',
                'nav.fallback.communicationAdmin',
                `${saPortalBase()}/communication`,
                'heroicons_outline:envelope'
            )
        );
    }

    const account: FuseNavigationItem[] = [
        // `exactMatch`: subset matching (Fuse default) kept this item lit on
        // every /me/* child page (notifications, invitations, privacy…), so
        // two account items were active at once.
        {
            ...basic(
                'me',
                'nav.fallback.myDashboard',
                '/me',
                'heroicons_outline:user-circle'
            ),
            exactMatch: true,
        },
        iamSecurityItem(),
        basic(
            'notifications',
            'nav.fallback.myNotifications',
            '/my-notifications',
            'heroicons_outline:bell'
        ),
        // Fixes: page was fully wired (GET/PUT users/me/notifications) but had no nav
        // entry anywhere, making it unreachable except by typing the URL directly.
        basic(
            'notification-preferences',
            'nav.fallback.notificationPreferences',
            '/me/notifications',
            'heroicons_outline:adjustments-horizontal'
        ),
        basic(
            'pending-invitations',
            'nav.fallback.pendingInvitations',
            '/me/invitations',
            'heroicons_outline:envelope-open'
        ),
        // WS-H (design ws-h-consent-design.md §6 bullet 1, PFL-2569 MR9): personal
        // "Privacy & AI" consent settings — same "would otherwise be unreachable
        // except by typing the URL" fix as notification-preferences above.
        basic(
            'privacy-consents',
            'nav.fallback.privacyConsents',
            '/me/privacy',
            'heroicons_outline:shield-check'
        ),
        // Drive connection and support tickets are shown only to roles their API policy
        // admits (be-app OrgMember / helpdesk HelpDesk.Read) — otherwise the page can only 403.
        ...(hasAnyRole(userRoles, [...ROLE_GROUPS.ORG_MEMBERS])
            ? [
                  basic(
                      'drive-connection',
                      'nav.fallback.connectGoogleDrive',
                      '/me/storage-connection',
                      'heroicons_outline:cloud'
                  ),
              ]
            : []),
        // D32(a) (role-billing-redesign 2026-09-23 plan §6.12/§8.1) — a NEW, narrower
        // customer-only "my tickets" surface at the same `/support` path PFL-1809 removed
        // (aae32154, the old admin-only Helpdesk chat widget).
        ...(canUseSupportTickets(userRoles)
            ? [
                  basic(
                      'support-tickets',
                      'nav.fallback.supportTickets',
                      '/support',
                      'heroicons_outline:chat-bubble-left-right'
                  ),
              ]
            : []),
        basic(
            'content-library',
            'nav.fallback.contentLibrary',
            '/content-library',
            'heroicons_outline:book-open'
        ),
    ];

    // Marketplace MVP (D39) — contract §4/§9, narrowed by design feedback
    // 2026-10-07: the whole Marketplace group is personal-session only
    // (SystemOrg). Org owners/admins no longer get marketplace nav — the
    // feature surface is platform-side, not studio-org. Routes still guard
    // themselves (personalSessionGuard on the shell + anyRoles on children).
    const marketplace: FuseNavigationItem[] = [];
    if (isPersonalMarketplaceUser) {
        marketplace.push(
            basic(
                'marketplace-profile',
                'nav.fallback.marketplaceProfile',
                '/marketplace/profile',
                'heroicons_outline:identification'
            ),
            basic(
                'marketplace-leads',
                'nav.fallback.marketplaceLeads',
                '/marketplace/leads',
                'heroicons_outline:inbox-arrow-down'
            ),
            basic(
                'marketplace-my-applications',
                'nav.fallback.marketplaceMyApplications',
                '/marketplace/my-applications',
                'heroicons_outline:document-text'
            )
        );
    }

    const nav: FuseNavigationItem[] = [];
    if (work.length) {
        nav.push(group('work', 'nav.fallback.work', work));
    }
    if (admin.length) {
        nav.push(group('admin', 'nav.fallback.organization', admin));
    }
    if (marketplace.length) {
        nav.push(group('marketplace', 'nav.fallback.marketplace', marketplace));
    }
    nav.push(group('account', 'nav.fallback.account', account));
    return nav;
}

/**
 * Pure SUPER_ADMIN platform navigation (photoflow-admin resource).
 *
 * fe-web has no SA-only internal pages left (T3.2/T3.3 of the fe-web →
 * sa-portal migration) — every Platform item is an external sister-shell
 * link. "System Overview" (/dashboard) and "Platform Audit" (superseded by
 * sa-portal's own /event-log nav item) were both removed, not repointed —
 * nothing left in fe-web to link to or redundant to keep.
 */
export function buildPhotoflowAdminFallbackNav(): FuseNavigationItem[] {
    return [
        group('platform', 'nav.fallback.platform', [
            sisterShell(
                'platform-admin',
                'nav.fallback.platformAdmin',
                saPortalBase(),
                'heroicons_outline:shield-check'
            ),
            iamPortalItem(),
        ]),
        group('account', 'nav.fallback.account', [iamSecurityItem()]),
    ];
}

/**
 * Nav item id → Unleash flag that hides it (mvp-release-scope-2026-10-13
 * §3.1/§5.2). The same flags guard the routes (`featureFlagGuard`), so a hidden
 * item is never one URL away. `/me/storage-connection` (id
 * `drive-connection`) is deliberately absent: album Drive import needs it.
 */
export const NAV_ITEM_FEATURE_FLAGS: Readonly<Record<string, FeatureFlag>> = {
    billing: FEATURE_FLAGS.BILLING,
    files: FEATURE_FLAGS.DAM,
    documents: FEATURE_FLAGS.DAM,
    'cloud-storage': FEATURE_FLAGS.DAM,
    chatbot: FEATURE_FLAGS.CHATBOT,
    'communication-admin': FEATURE_FLAGS.COMMUNICATION_ADMIN,
    marketplace: FEATURE_FLAGS.MARKETPLACE,
    'marketplace-profile': FEATURE_FLAGS.MARKETPLACE,
    'marketplace-leads': FEATURE_FLAGS.MARKETPLACE,
    'marketplace-my-applications': FEATURE_FLAGS.MARKETPLACE,
};

/**
 * Drops nav items whose feature flag is off (`NAV_ITEM_FEATURE_FLAGS`), and any
 * group/collapsable left with no children by that. Returns new objects; the
 * input tree is not mutated.
 */
export function filterNavByFeatureFlags(
    items: FuseNavigationItem[],
    isEnabled: (flag: FeatureFlag) => boolean
): FuseNavigationItem[] {
    const kept: FuseNavigationItem[] = [];
    for (const item of items) {
        const flag = item.id ? NAV_ITEM_FEATURE_FLAGS[item.id] : undefined;
        if (flag && !isEnabled(flag)) {
            continue;
        }
        if (!item.children?.length) {
            kept.push(item);
            continue;
        }
        const children = filterNavByFeatureFlags(item.children, isEnabled);
        if (children.length) {
            kept.push({ ...item, children });
        }
    }
    return kept;
}

/**
 * Resolve the `nav.fallback.*` title keys the builders emit into display text.
 *
 * Apply to fallback trees only — Menu-API titles arrive already localized from the
 * backend and are not keys. Returns new objects rather than mutating, so the pure
 * builder output stays reusable.
 */
export function localizeNavTitles(
    items: FuseNavigationItem[],
    translate: (key: string) => string
): FuseNavigationItem[] {
    return items.map((item) => ({
        ...item,
        ...(item.title ? { title: translate(item.title) } : {}),
        ...(item.children
            ? { children: localizeNavTitles(item.children, translate) }
            : {}),
    }));
}
